import { Dispatch, MutableRefObject, SetStateAction, useCallback, useRef, useState } from 'react';

export interface ChatConversationStateOptions<TConversation> {
  sortComparator?: (a: TConversation, b: TConversation) => number;
  getConversationId?: (conversation: TConversation) => string;
}

const defaultGetConversationId = (conversation: unknown): string => {
  if (typeof conversation === 'object' && conversation !== null) {
    const conv = conversation as Record<string, unknown>;
    const conversationId = conv.conversation_id ?? conv.id;
    return typeof conversationId === 'string' ? conversationId : '';
  }
  return '';
};

export interface ChatConversationState<TConversation> {
  conversations: TConversation[];
  setConversations: Dispatch<SetStateAction<TConversation[]>>;
  activeConversationId: string;
  setActiveConversationId: (conversationId: string) => void;
  activeConversationRef: MutableRefObject<string>;
  sortConversations: (input: TConversation[]) => TConversation[];
  getConversationId: (conversation: TConversation) => string;
}

export const useChatConversationState = <TConversation>({
  sortComparator,
  getConversationId = defaultGetConversationId,
}: ChatConversationStateOptions<TConversation> = {}): ChatConversationState<TConversation> => {
  const [conversations, setConversationsState] = useState<TConversation[]>([]);
  const [activeConversationId, setActiveConversationIdState] = useState('');
  const activeConversationRef = useRef('');

  const sortConversations = useCallback(
    (input: TConversation[]) => {
      if (!input.length) {
        return [];
      }

      const clone = [...input];
      if (sortComparator) {
        clone.sort(sortComparator);
      }
      return clone;
    },
    [sortComparator]
  );

  const setConversations = useCallback<Dispatch<SetStateAction<TConversation[]>>>(
    (updater) => {
      setConversationsState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (!Array.isArray(next)) {
          return prev;
        }
        return sortConversations(next);
      });
    },
    [sortConversations]
  );

  const setActiveConversationId = useCallback((conversationId: string) => {
    activeConversationRef.current = conversationId;
    setActiveConversationIdState(conversationId);
  }, []);

  return {
    conversations,
    setConversations,
    activeConversationId,
    setActiveConversationId,
    activeConversationRef,
    sortConversations,
    getConversationId,
  };
};
